import { useMapboxEffect } from '@hooks/use-mapbox';
import { useId, useRef } from 'react';
import { loadMapboxImage } from '../util';
import pinIcon from '../map-pin.png';
import pinSelectedIcon from '../map-pin-selected.png';
import { assert, noop } from '@crocodille/util';
import { MapLayerMouseEvent } from 'mapbox-gl';

export interface BranchLocation {
  readonly location: [longitude: number, latitude: number];
  readonly code: string;
}

export interface BranchLocationPluginProps {
  readonly branchLocations: readonly BranchLocation[];
  readonly selectedLocationCode?: string | null;
  onLocationSelected?(code: string): void;
}

function locationsToGeoJSON(branchLocations: readonly BranchLocation[]) {
  return {
    type: 'FeatureCollection' as const,
    features: branchLocations.map((bl) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: bl.location,
      },
      properties: {
        codes: [bl.code],
      },
    })),
  };
}

const BranchLocationPlugin: React.FC<BranchLocationPluginProps> = ({
  branchLocations,
  selectedLocationCode,
  onLocationSelected = noop,
}) => {
  const branchLocationsRef = useRef(branchLocations);
  branchLocationsRef.current = branchLocations;

  const selectedLocationCodeRef = useRef(selectedLocationCode);
  selectedLocationCodeRef.current = selectedLocationCode;

  const dataSourceId = useId();
  const layerId = useId();
  const iconSourceId = useId();
  const iconSelectedSourceId = useId();

  useMapboxEffect((map) => {
    map.addSource(dataSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection' as const,
        features: [],
      },
    });

    Promise.all([
      loadMapboxImage(map, pinIcon.src),
      loadMapboxImage(map, pinSelectedIcon.src),
    ]).then(([loadedPinIcon, loadedPinSelectedIcon]) => {
      map.addImage(iconSourceId, loadedPinIcon);
      map.addImage(iconSelectedSourceId, loadedPinSelectedIcon);

      map.addLayer({
        id: layerId,
        type: 'symbol',
        source: dataSourceId,
        layout: {
          'icon-image': [
            'case',
            ['in', selectedLocationCodeRef.current ?? '', ['get', 'codes']],
            iconSelectedSourceId,
            iconSourceId,
          ],
          'icon-size': 0.5,
          'icon-anchor': 'top',
          'icon-offset': [0, -40 / 0.5 /* Icon Scale */],
          'icon-allow-overlap': true,
        },
      });
    });

    const onClick = (e: MapLayerMouseEvent) => {
      const properties = e.features?.[0]?.properties;
      const codes = properties?.codes as string;

      const code = JSON.parse(codes)[0];

      if (code) {
        onLocationSelected(code);
      }
    };

    map.on('click', layerId, onClick);

    const onMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    map.on('mouseenter', layerId, onMouseEnter);

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('mouseleave', layerId, onMouseLeave);

    return () => {
      map.off('click', layerId, onClick);
      map.off('mouseenter', layerId, onMouseEnter);
      map.off('mouseleave', layerId, onMouseLeave);

      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }

      if (map.hasImage(iconSourceId)) {
        map.removeImage(iconSourceId);
      }

      if (map.hasImage(iconSelectedSourceId)) {
        map.removeImage(iconSelectedSourceId);
      }

      map.removeSource(dataSourceId);
    };
  }, []);

  useMapboxEffect(
    (map) => {
      const dataSource = map.getSource(dataSourceId);

      assert(dataSource?.type === 'geojson');
      dataSource.setData(locationsToGeoJSON(branchLocations));
    },
    [branchLocations],
  );

  useMapboxEffect(
    (map) => {
      if (!map.getLayer(layerId)) {
        return;
      }

      map.setLayoutProperty(layerId, 'icon-image', [
        'case',
        ['in', selectedLocationCode ?? '', ['get', 'codes']],
        iconSelectedSourceId,
        iconSourceId,
      ]);

      const selected = branchLocationsRef.current.find(
        (bl) => bl.code === selectedLocationCode,
      );

      if (selected) {
        map.flyTo({
          center: selected.location,
        });
      }
    },
    [selectedLocationCode],
  );

  return null;
};

export default BranchLocationPlugin;
